/**
 * SupXRef hover popups for article reference citations.
 * Reference content is supplied via [data-article-references] on the article element.
 */

import { scrollToPosition } from '../utils/scrollToPosition.js';

/** @type {string} */
export const ARTICLE_SCROLL_TO_HASH_EVENT = 'mja:scroll-to-hash';

const ARTICLE_REFERENCE_SCROLL_OFFSET = 12;

/** @type {boolean} */
let scrollFromModalBound = false;

/** @type {HTMLElement | null} */
let popupEl = null;
/** @type {ReturnType<typeof setTimeout> | null} */
let hideTimer = null;
/** @type {HTMLElement | null} */
let activeLink = null;

function ensurePopup() {
  if (popupEl) {
    return popupEl;
  }

  popupEl = document.createElement('div');
  popupEl.className = 'article-ref-popup';
  popupEl.hidden = true;
  popupEl.setAttribute('role', 'tooltip');
  document.body.appendChild(popupEl);

  popupEl.addEventListener('mouseenter', () => {
    if (hideTimer) {
      window.clearTimeout(hideTimer);
      hideTimer = null;
    }
  });

  popupEl.addEventListener('mouseleave', () => {
    hidePopup();
  });

  return popupEl;
}

function hidePopup() {
  if (hideTimer) {
    window.clearTimeout(hideTimer);
    hideTimer = null;
  }

  if (popupEl) {
    popupEl.hidden = true;
    popupEl.innerHTML = '';
  }

  activeLink = null;
}

function scheduleHide() {
  if (hideTimer) {
    window.clearTimeout(hideTimer);
  }

  hideTimer = window.setTimeout(() => {
    hidePopup();
  }, 120);
}

/**
 * @param {HTMLElement} link
 */
function positionPopup(link) {
  if (!popupEl || popupEl.hidden) {
    return;
  }

  const rect = link.getBoundingClientRect();
  const popupRect = popupEl.getBoundingClientRect();
  const viewportPadding = 12;
  const gap = 8;

  let left = rect.left + rect.width / 2 - popupRect.width / 2;
  left = Math.max(viewportPadding, Math.min(left, window.innerWidth - popupRect.width - viewportPadding));

  let top = rect.top - popupRect.height - gap;
  if (rect.top - popupRect.height - gap < viewportPadding) {
    top = rect.bottom + gap;
    popupEl.dataset.placement = 'bottom';
  } else {
    popupEl.dataset.placement = 'top';
  }

  popupEl.style.left = `${left}px`;
  popupEl.style.top = `${top}px`;
}

/**
 * @param {HTMLElement} link
 * @param {string} html
 */
function showPopup(link, html) {
  const popup = ensurePopup();
  popup.innerHTML = html;
  popup.hidden = false;
  activeLink = link;

  requestAnimationFrame(() => {
    positionPopup(link);
  });
}

/**
 * @param {Record<string, string>} refMap
 * @param {HTMLElement} link
 */
function resolveReferenceHtml(refMap, link) {
  const hrefId = (link.getAttribute('href') || '').replace(/^#/, '').trim();
  const textId = (link.textContent || '').trim();
  return refMap[hrefId] || refMap[textId] || '';
}

/**
 * @param {HTMLElement} article
 * @param {Record<string, string>} refMap
 * @param {HTMLElement} link
 */
function activateReferenceLink(article, refMap, link) {
  if (!(link instanceof HTMLAnchorElement) || !article.contains(link)) {
    return;
  }

  const html = resolveReferenceHtml(refMap, link);
  if (!html) {
    return;
  }

  if (hideTimer) {
    window.clearTimeout(hideTimer);
    hideTimer = null;
  }

  showPopup(link, html);
}

/**
 * @param {string} hash
 * @param {number} [extraOffset]
 */
function scrollToArticleHash(hash, extraOffset = ARTICLE_REFERENCE_SCROLL_OFFSET) {
  if (!hash.startsWith('#')) {
    return;
  }

  scrollToPosition(hash, extraOffset, '.c-header', 0, { preferHeaderOffset: true });
}

/**
 * Smooth scroll for in-article hash links (references, legacy boxes, etc.).
 * @param {HTMLElement} article
 */
function initArticleSmoothScroll(article) {
  if (article.dataset.articleSmoothScrollBound === 'true') {
    return;
  }

  article.addEventListener('click', (event) => {
    const link =
      event.target instanceof Element
        ? event.target.closest('a[href^="#"]:not([href="#"])')
        : null;

    if (!link || !article.contains(link)) {
      return;
    }

    const href = link.getAttribute('href') || '';

    event.preventDefault();
    hidePopup();
    scrollToArticleHash(href);
  });

  article.dataset.articleSmoothScrollBound = 'true';
}

/**
 * @param {HTMLElement} article
 */
function initArticleReferencePopups(article) {
  if (article.dataset.articleRefsBound === 'true') {
    return;
  }

  /** @type {Array<{id: string|number, html: string}>} */
  let items = [];

  try {
    items = JSON.parse(article.getAttribute('data-article-references') || '[]');
  } catch {
    return;
  }

  if (!items.length) {
    return;
  }

  /** @type {Record<string, string>} */
  const refMap = {};
  items.forEach((item) => {
    refMap[String(item.id)] = item.html;
  });

  article.addEventListener(
    'mouseover',
    (event) => {
      const link = event.target instanceof Element ? event.target.closest('a.SupXRef') : null;
      if (!link || !article.contains(link)) {
        return;
      }

      activateReferenceLink(article, refMap, link);
    },
    true,
  );

  article.addEventListener(
    'mouseout',
    (event) => {
      const link = event.target instanceof Element ? event.target.closest('a.SupXRef') : null;
      if (!link || !article.contains(link)) {
        return;
      }

      const related = event.relatedTarget instanceof Node ? event.relatedTarget : null;
      if (related && (link.contains(related) || popupEl?.contains(related))) {
        return;
      }

      scheduleHide();
    },
    true,
  );

  article.addEventListener(
    'focusin',
    (event) => {
      const link = event.target instanceof Element ? event.target.closest('a.SupXRef') : null;
      if (!link || !article.contains(link)) {
        return;
      }

      activateReferenceLink(article, refMap, link);
    },
    true,
  );

  article.addEventListener(
    'focusout',
    (event) => {
      const link = event.target instanceof Element ? event.target.closest('a.SupXRef') : null;
      if (!link || !article.contains(link)) {
        return;
      }

      scheduleHide();
    },
    true,
  );

  article.dataset.articleRefsBound = 'true';
}

function bindScrollFromModal() {
  if (scrollFromModalBound) {
    return;
  }

  scrollFromModalBound = true;

  document.addEventListener(ARTICLE_SCROLL_TO_HASH_EVENT, (event) => {
    const hash = event.detail?.hash;

    if (typeof hash !== 'string' || !hash.startsWith('#')) {
      return;
    }

    hidePopup();
    scrollToArticleHash(hash, event.detail?.extraOffset ?? ARTICLE_REFERENCE_SCROLL_OFFSET);
  });
}

function initArticleReferences() {
  bindScrollFromModal();

  document.querySelectorAll('article').forEach((article) => {
    if (!(article instanceof HTMLElement) || !article.querySelector('.content-blocks--article')) {
      return;
    }

    initArticleSmoothScroll(article);

    if (article.hasAttribute('data-article-references')) {
      initArticleReferencePopups(article);
    }
  });

  window.addEventListener(
    'scroll',
    () => {
      if (activeLink && popupEl && !popupEl.hidden) {
        positionPopup(activeLink);
      }
    },
    { passive: true },
  );

  window.addEventListener('resize', () => {
    if (activeLink && popupEl && !popupEl.hidden) {
      positionPopup(activeLink);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      hidePopup();
    }
  });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initArticleReferences);
  } else {
    initArticleReferences();
  }
}
